import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import { fetchData } from "../../../api/client";
import { useTypes } from "../../../hooks/useTypes";

function flattenTypesTree(nodes, parentId = null) {
  if (!Array.isArray(nodes)) return [];
  return nodes.flatMap((n) => {
    const id = n.id;
    const idPai = n.id_pai != null && n.id_pai !== "" ? Number(n.id_pai) : parentId;
    const { children: _c, ...rest } = n;
    const item = { ...rest, id: Number(id), id_pai: idPai };
    return [item, ...flattenTypesTree(n.children || [], Number(id))];
  });
}

const ENTITY_TYPES = [
  { value: "lead", label: "Solicitação" },
  { value: "client", label: "Cliente" },
];

const SCOPES = [
  { value: "workspace", label: "Workspace (todos)" },
  { value: "tipo", label: "Tipo de atendimento" },
  { value: "subtipo1", label: "Subtipo 1" },
  { value: "subtipo2", label: "Subtipo 2" },
  { value: "subtipo3", label: "Subtipo 3" },
];

const FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "date",
  "datetime",
  "boolean",
  "select",
  "multiselect",
];

const emptyForm = () => ({
  entityType: "lead",
  scope: "tipo",
  scopeId: "",
  fieldKey: "",
  label: "",
  fieldType: "text",
  optionsText: "",
  required: false,
  sortOrder: 0,
});

const CustomFieldDefinitionsSettings = ({
  presetScope = null,
  presetScopeId = null,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const { data: typesData } = useTypes(1, 500, "");
  const typesFlat = React.useMemo(
    () => flattenTypesTree(typesData?.types ?? []),
    [typesData?.types]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchData("/custom-fields/definitions", "GET");
      setItems(res?.data?.items || res?.items || []);
      setError(null);
    } catch (e) {
      setError(e?.message || "Erro ao carregar definições.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    const base = emptyForm();
    if (presetScope) base.scope = presetScope;
    if (presetScopeId != null && presetScopeId !== "") {
      base.scopeId = String(presetScopeId);
    }
    setForm(base);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    let optionsText = "";
    if (row.optionsJson) {
      try {
        const parsed =
          typeof row.optionsJson === "string"
            ? JSON.parse(row.optionsJson)
            : row.optionsJson;
        optionsText = Array.isArray(parsed)
          ? parsed.join("\n")
          : Array.isArray(parsed?.items)
          ? parsed.items.join("\n")
          : "";
      } catch (_) {
        optionsText = "";
      }
    }
    setForm({
      entityType: row.entityType,
      scope: row.scope,
      scopeId: row.scopeId ?? "",
      fieldKey: row.fieldKey,
      label: row.label,
      fieldType: row.fieldType,
      optionsText,
      required: row.required,
      sortOrder: row.sortOrder ?? 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const options = form.optionsText
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const body = {
      entityType: form.entityType,
      scope: form.scope,
      scopeId: form.scopeId ? Number(form.scopeId) : null,
      fieldKey: form.fieldKey.trim(),
      label: form.label.trim(),
      fieldType: form.fieldType,
      options: options.length ? options : null,
      required: form.required,
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (editingId) {
        await fetchData(`/custom-fields/definitions/${editingId}`, "PUT", body);
      } else {
        await fetchData("/custom-fields/definitions", "POST", body);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      setError(e?.message || "Erro ao salvar campo.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remover este campo personalizado?")) return;
    try {
      await fetchData(`/custom-fields/definitions/${id}`, "DELETE");
      load();
    } catch (e) {
      setError(e?.message || "Erro ao remover.");
    }
  };

  const scopeOptions =
    form.scope === "workspace"
      ? [{ id: "", nome: "—" }]
      : typesFlat.filter((t) => {
          if (form.scope === "tipo") return !t.id_pai || Number(t.id_pai) === 0;
          return true;
        });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5">Campos personalizados por tipo</Typography>
          <Typography variant="body2" color="text.secondary">
            Campos extras além dos slots legado (produto, campanha, data/texto adicional).
            Valores gravados em tabela EAV; não migra dados históricos.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          Novo campo
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Entidade</TableCell>
                <TableCell>Escopo</TableCell>
                <TableCell>Chave</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nenhum campo personalizado cadastrado.
                  </TableCell>
                </TableRow>
              )}
              {items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.entityType === "lead" ? "Solicitação" : "Cliente"}</TableCell>
                  <TableCell>
                    {row.scope}
                    {row.scopeId != null ? ` #${row.scopeId}` : ""}
                  </TableCell>
                  <TableCell>{row.fieldKey}</TableCell>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>{row.fieldType}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(row.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? "Editar campo" : "Novo campo personalizado"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            select
            label="Entidade"
            value={form.entityType}
            onChange={(e) => setForm((p) => ({ ...p, entityType: e.target.value }))}
          >
            {ENTITY_TYPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Escopo"
            value={form.scope}
            onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value, scopeId: "" }))}
          >
            {SCOPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          {form.scope !== "workspace" && (
            <TextField
              select
              label="ID do escopo (tipo/subtipo)"
              value={form.scopeId}
              onChange={(e) => setForm((p) => ({ ...p, scopeId: e.target.value }))}
            >
              {scopeOptions.map((t) => (
                <MenuItem key={t.id} value={String(t.id)}>
                  {t.nome || t.name || t.id}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Chave (field_key)"
            value={form.fieldKey}
            disabled={!!editingId}
            onChange={(e) => setForm((p) => ({ ...p, fieldKey: e.target.value }))}
            helperText="Identificador único no escopo (ex.: numero_sinistro)"
          />
          <TextField
            label="Label na tela"
            value={form.label}
            onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
          />
          <TextField
            select
            label="Tipo"
            value={form.fieldType}
            onChange={(e) => setForm((p) => ({ ...p, fieldType: e.target.value }))}
          >
            {FIELD_TYPES.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
          {(form.fieldType === "select" || form.fieldType === "multiselect") && (
            <TextField
              label="Opções (uma por linha)"
              multiline
              minRows={3}
              value={form.optionsText}
              onChange={(e) => setForm((p) => ({ ...p, optionsText: e.target.value }))}
            />
          )}
          <TextField
            label="Ordem"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((p) => ({ ...p, sortOrder: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CustomFieldDefinitionsSettings;
